import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import appAPI from '../../api/api'
import { commentVersion, ForumCommentRead, ForumPinnedComment, ForumTopicReadDetail } from '../../api/forum-comment'
import { RootState } from '../store'
import { UserWSRetrieveForCommentForum } from '../../api/model/userWSRetrieveForCommentForum'
import { CUSTOM_OPTION_ID } from '../../components/forum/detail-view/topic-poll/poll-detail/poll-detail-questions-section/one-of-choice/one-of-choice'

type ForumTopicState = Partial<ForumTopicReadDetail> & {
  loading: boolean
  requestId?: string
  error?: string
}

export const initialForumTopicState: ForumTopicState = {
  loading: false,
}

const sortPins = (pins: ForumPinnedComment[]) =>
  [...pins].sort((a, b) => commentVersion(b.created || '') - commentVersion(a.created || '') || b.id - a.id)

const fetchForumTopic = createAsyncThunk('/fetchForumTopic', async (payload: { id: number }, { rejectWithValue }) => {
  try {
    return await appAPI.forumTopic.getTopic(payload.id)
  } catch (err: any) {
    return rejectWithValue({ messages: err?.response?.data, status: err?.response?.status })
  }
})

const ForumTopicSlice = createSlice({
  name: 'topic',
  initialState: initialForumTopicState,
  reducers: {
    setDialogClosed: (state, { payload }: PayloadAction<{ id: number; isClosed: boolean }>) => {
      if (state.id === payload.id && state.isDirect) state.isClosed = payload.isClosed
    },
    clearTopic: () => {
      return initialForumTopicState
    },
    setPinnedCommentFromMessage: (state, { payload }: PayloadAction<ForumCommentRead>) => {
      const pins = (state.pinnedComments || []).filter((pin) => pin.id !== payload.id)
      if (payload.isPinned) pins.push(payload)
      state.pinnedComments = sortPins(pins)
    },
    clearPinnedComment: (state, { payload }: PayloadAction<number>) => {
      state.pinnedComments = (state.pinnedComments || []).filter((pin) => pin.id !== payload)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchForumTopic.pending, (state, { meta }) => ({ loading: true, requestId: meta.requestId }))
      .addCase(fetchForumTopic.fulfilled, (state, { payload, meta }) => {
        if (state.requestId !== meta.requestId) return
        return {
          ...payload.data,
          pinnedComments: sortPins(payload.data.pinnedComments || []),
          loading: false,
        }
      })
      .addCase(fetchForumTopic.rejected, (state, { payload, meta }) => {
        if (state.requestId !== meta.requestId) return
        return {
          loading: false,
          error:
            (payload as { status?: number })?.status === 403
              ? 'Доступ к обсуждению закрыт.'
              : 'Не удалось загрузить обсуждение. Попробуйте открыть его снова.',
        }
      })
  },
})

export default ForumTopicSlice.reducer

export const forumTopicActions = {
  ...ForumTopicSlice.actions,
  fetchForumTopic,
}

type selectTopicMetaForPoll = {
  title: string
  loading: boolean
  author: UserWSRetrieveForCommentForum
  createdAt: {
    date: string
    time: string
  }
}
export const selectTopicMetaForPoll = (state: RootState): selectTopicMetaForPoll => {
  const date = new Date(state.forumTopic.created || '').toLocaleDateString('ru-Ru')
  const time = new Date(state.forumTopic.created || '').toLocaleTimeString('ru-Ru', {
    hour: '2-digit',
    minute: '2-digit',
  })

  return {
    title: state.forumTopic.title || '',
    loading: state.forumTopic.loading,
    author: state.forumTopic.author as UserWSRetrieveForCommentForum,
    createdAt: {
      date,
      time,
    },
  }
}

export const selectUserVoteForOneOfChoice = (state: RootState): number | undefined => {
  const customOption = state.forumTopic.poll?.userVote?.customOption

  if (customOption) return CUSTOM_OPTION_ID
  const options = state.forumTopic.poll?.userVote?.options || []

  return options?.length > 0 ? options[0] : undefined
}
